import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Modal from '@/components/base/Modal';
import ReceiptModal from '@/components/feature/ReceiptModal';
import { downloadShippingLabel } from '@/lib/shippingLabel';
import { useBrandIdentity } from '@/hooks/useBrandIdentity';
import { fetchSaleDetail, voidSale, markSaleAsPaid, SALE_PAYMENT_METHOD_LABELS, type SaleDetail } from '@/services/sales';
import { useAuth } from '@/context/AuthContext';

interface SaleDetailModalProps {
  saleId: string;
  onClose: () => void;
  onChanged?: () => void;
}

export default function SaleDetailModal({ saleId, onClose, onChanged }: SaleDetailModalProps) {
  const { profile } = useAuth();
  const isAdmin = profile?.role === 'admin';
  const [detail, setDetail] = useState<SaleDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [voidError, setVoidError] = useState('');
  const [markingPaid, setMarkingPaid] = useState(false);
  const [markPaidError, setMarkPaidError] = useState('');
  const [showReceipt, setShowReceipt] = useState(false);
  const [autoPrintReceipt, setAutoPrintReceipt] = useState(false);
  const identity = useBrandIdentity();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchSaleDetail(saleId)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setError('No se pudo cargar el detalle de la venta.');
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [saleId]);

  async function handleVoid() {
    setVoiding(true);
    setVoidError('');
    const res = await voidSale(saleId, voidReason);
    setVoiding(false);
    if (!res.success) {
      setVoidError(res.error || 'No se pudo anular la venta.');
      return;
    }
    onChanged?.();
    onClose();
  }

  async function handleMarkPaid() {
    setMarkingPaid(true);
    setMarkPaidError('');
    const res = await markSaleAsPaid(saleId);
    setMarkingPaid(false);
    if (res.error) {
      setMarkPaidError(res.error);
      return;
    }
    onChanged?.();
    onClose();
  }

  return (
    <>
    <Modal
      title="Detalle de Venta"
      subtitle={detail?.saleNumber ?? 'Cargando...'}
      onClose={onClose}
      maxWidth="max-w-2xl"
    >
      {loading ? (
        <div className="text-center py-12 text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-2xl block mb-2"></i>
          <p className="text-sm">Cargando detalle...</p>
        </div>
      ) : error || !detail ? (
        <div className="text-center py-12 text-foreground-500">
          <i className="ri-error-warning-line text-3xl block mb-2"></i>
          <p className="text-sm">{error || 'No se encontró la venta.'}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {detail.voided && (
            <div className="flex items-start gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg px-3 py-2.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              <div>
                <span>Esta venta está anulada y no se computa en los registros contables.</span>
                {detail.voidReason && (
                  <span className="block text-xs text-red-600 mt-1">
                    <strong>Motivo:</strong> {detail.voidReason}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Quién y por qué medio */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Cliente</p>
              <p className="text-sm font-medium text-foreground-900">{detail.customerName || '—'}</p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Vendedor</p>
              <p className="text-sm font-medium text-foreground-900">{detail.salespersonName || '—'}</p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Canal</p>
              <p className="text-sm font-medium text-foreground-900 capitalize">
                {detail.channel === 'ecommerce' ? 'Comercio electrónico' : 'Venta directa'}
              </p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Fecha</p>
              <p className="text-sm font-medium text-foreground-900">
                {detail.saleDate
                  ? new Date(detail.saleDate).toLocaleDateString('es-CO', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })
                  : '—'}
              </p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Medio de pago</p>
              <p className="text-sm font-medium text-foreground-900">
                {detail.paymentMethod
                  ? SALE_PAYMENT_METHOD_LABELS[detail.paymentMethod] || detail.paymentMethod
                  : '—'}
              </p>
            </div>
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-xs text-foreground-500">Estado de pago</p>
              {detail.paymentStatus === 'approved' ? (
                <p className="text-sm font-medium text-emerald-700 flex items-center gap-1">
                  <i className="ri-checkbox-circle-line"></i>Pagado
                </p>
              ) : detail.paymentStatus === 'pending' ? (
                <p className="text-sm font-medium text-amber-700 flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {detail.paymentMethod === 'cash' ? 'Contra entrega' : 'Pendiente'}
                </p>
              ) : (
                <p className="text-sm text-foreground-500">—</p>
              )}
            </div>
          </div>

          {/* Pedido de producción vinculado (productos sin stock) */}
          {detail.productionOrderNumber && (
            <div className="flex items-center gap-2 text-sm bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5">
              <i className="ri-tools-line text-amber-600"></i>
              <span className="text-amber-800">
                Incluye productos sin stock enviados a producción:{' '}
                <strong>{detail.productionOrderNumber}</strong>
              </span>
              <Link
                to="/admin/produccion"
                className="ml-auto text-xs text-amber-700 hover:text-amber-900 font-medium whitespace-nowrap"
              >
                Ver producción
              </Link>
            </div>
          )}

          {/* Items */}
          <div>
            <p className="text-sm font-semibold text-foreground-900 mb-2">Productos vendidos</p>
            {detail.items.length === 0 ? (
              <p className="text-sm text-foreground-500">Sin líneas registradas.</p>
            ) : (
              <div className="border border-background-200 rounded-lg overflow-hidden overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="border-b border-background-100 bg-background-50 text-left">
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500">Producto</th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500">Talla</th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500 text-right">
                        Cant.
                      </th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500 text-right">
                        P. Unit.
                      </th>
                      <th className="px-3 py-2 text-xs font-medium text-foreground-500 text-right">
                        Subtotal
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, idx) => (
                      <tr key={idx} className="border-b border-background-100 last:border-0">
                        <td className="px-3 py-2 text-foreground-900">{it.productName}</td>
                        <td className="px-3 py-2 text-foreground-600">{it.sizeName || '—'}</td>
                        <td className="px-3 py-2 text-right text-foreground-700">{it.quantity}</td>
                        <td className="px-3 py-2 text-right text-foreground-700">
                          ${it.unitPrice.toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right text-foreground-900 font-medium">
                          ${it.subtotal.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagos */}
          {detail.payments.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-foreground-900 mb-2">Pagos</p>
              <div className="space-y-1.5">
                {detail.payments.map((p, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between border border-background-200 rounded-md px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-foreground-700 capitalize">{p.method}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-background-100 text-foreground-600 capitalize">
                        {p.status}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-foreground-900">
                      ${p.amount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Datos de envío (Interrapidísimo) */}
          {detail.shipping && (
            <div className="border border-background-200 rounded-lg p-3">
              <p className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-1.5">
                <i className="ri-truck-line text-accent-600"></i>Datos de envío (Interrapidísimo)
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <div className="flex justify-between gap-3 sm:col-span-2">
                  <span className="text-foreground-500">Destinatario</span>
                  <span className="font-medium text-foreground-900 text-right">{detail.shipping.recipientName || '—'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-foreground-500">Celular</span>
                  <span className="font-medium text-foreground-900">{detail.shipping.recipientPhone || '—'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-foreground-500">Cédula / NIT</span>
                  <span className="font-medium text-foreground-900">{detail.shipping.recipientIdNumber || '—'}</span>
                </div>
                <div className="flex justify-between gap-3 sm:col-span-2">
                  <span className="text-foreground-500">Ciudad / Departamento</span>
                  <span className="font-medium text-foreground-900 text-right">
                    {[detail.shipping.city, detail.shipping.department].filter(Boolean).join(' — ') || '—'}
                  </span>
                </div>
                <div className="flex justify-between gap-3 sm:col-span-2">
                  <span className="text-foreground-500">Dirección</span>
                  <span className="font-medium text-foreground-900 text-right">{detail.shipping.address || '—'}</span>
                </div>
                {detail.shipping.notes && (
                  <div className="flex justify-between gap-3 sm:col-span-2">
                    <span className="text-foreground-500">Observaciones</span>
                    <span className="font-medium text-foreground-900 text-right">{detail.shipping.notes}</span>
                  </div>
                )}
                <div className="flex justify-between gap-3 sm:col-span-2 pt-2 border-t border-background-100">
                  <span className="text-foreground-500">Flete cobrado</span>
                  <span className="font-medium text-foreground-900">${detail.shippingCost.toLocaleString()}</span>
                </div>
              </div>
            </div>
          )}

          {/* Totales */}
          <div className="border-t border-background-200 pt-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Costo total</span>
              <span className="text-foreground-700">${detail.costTotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-foreground-500">Utilidad bruta</span>
              <span className="text-emerald-600 font-medium">
                ${detail.grossProfit.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between text-base font-semibold">
              <span className="text-foreground-900">Total</span>
              <span className="text-foreground-950">${detail.total.toLocaleString()}</span>
            </div>
          </div>

          {/* Comprobante y Guía */}
          <div className="border-t border-background-200 pt-3 flex flex-col sm:flex-row gap-2">
            <button
              onClick={() => {
                setAutoPrintReceipt(false);
                setShowReceipt(true);
              }}
              className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-download-2-line mr-1.5"></i>Descargar comprobante
            </button>
            <button
              onClick={() => {
                setAutoPrintReceipt(true);
                setShowReceipt(true);
              }}
              className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
            >
              <i className="ri-printer-line mr-1.5"></i>Imprimir
            </button>
            {detail && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  downloadShippingLabel({
                    companyName: identity.companyName,
                    companyPhone: identity.companyPhone,
                    companyCity: identity.companyCity,
                    companyLogo: identity.logo,
                    senderName: identity.senderName,
                    senderId: identity.senderId,
                    senderPhone: identity.senderPhone,
                    senderAddress: identity.senderAddress,
                    senderCity: identity.senderCity,
                    recipientName: detail.shipping?.recipientName || detail.customerName,
                    recipientPhone: detail.shipping?.recipientPhone || '',
                    recipientId: detail.shipping?.recipientIdNumber || '',
                    recipientEmail: detail.shipping?.recipientEmail || '',
                    recipientCity: detail.shipping?.city || '',
                    recipientDepartment: detail.shipping?.department || '',
                    recipientAddress: detail.shipping?.address || '',
                    recipientNotes: detail.shipping?.notes || '',
                    orderNumber: detail.saleNumber,
                    orderDate: detail.saleDate,
                    contents: detail.items.map(it => `${it.productName} · ${it.sizeName} × ${it.quantity}`).join(', '),
                    paymentMode: detail.paymentMethod === 'cash' ? 'Cobro Contraentrega' : 'Flete Pagado',
                    total: detail.paymentMethod === 'cash' ? detail.total : 0,
                  });
                }}
                className="flex-1 text-sm px-4 py-2.5 border border-background-300 text-foreground-700 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                <i className="ri-file-paper-2-line mr-1.5"></i>Guía de Envío
              </button>
            )}
          </div>

          {/* Marcar como pagado (pago pendiente / contra entrega) */}
          {!detail.voided && detail.paymentStatus === 'pending' && (
            <div className="border-t border-background-200 pt-3 space-y-2.5">
              <div className="flex items-start gap-2 text-sm bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-2.5">
                <i className="ri-time-line mt-0.5"></i>
                <span>
                  {detail.paymentMethod === 'cash'
                    ? 'Esta venta es a contra entrega y aún está pendiente de cobro.'
                    : 'Esta venta tiene un pago pendiente.'}
                </span>
              </div>
              {markPaidError && (
                <p className="text-xs text-red-700 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {markPaidError}
                </p>
              )}
              <button
                onClick={handleMarkPaid}
                disabled={markingPaid}
                className="text-sm px-4 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors whitespace-nowrap font-medium cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {markingPaid ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Marcando...
                  </>
                ) : (
                  <>
                    <i className="ri-checkbox-circle-line mr-1.5"></i>Marcar como pagado
                  </>
                )}
              </button>
            </div>
          )}

          {/* Anulación */}
          {!detail.voided && isAdmin && !confirmingVoid && (
            <div className="border-t border-background-200 pt-3">
              <button
                onClick={() => {
                  setConfirmingVoid(true);
                  setVoidError('');
                }}
                className="text-sm px-4 py-2.5 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap font-medium cursor-pointer"
              >
                <i className="ri-close-circle-line mr-1.5"></i>Anular venta
              </button>
            </div>
          )}

          {confirmingVoid && !detail.voided && (
            <div className="border border-red-200 bg-red-50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2.5">
                <i className="ri-alert-line text-red-600 text-lg mt-0.5"></i>
                <div>
                  <p className="text-sm font-semibold text-red-800">¿Anular esta venta?</p>
                  <p className="text-xs text-red-700 mt-0.5">
                    Se devolverá el stock de los productos, se eliminará la comisión asociada y la
                    venta dejará de contar en los registros contables. Esta acción no se puede
                    deshacer.
                  </p>
                </div>
              </div>
              {voidError && (
                <p className="text-xs text-red-700 flex items-center gap-1.5">
                  <i className="ri-error-warning-line"></i>
                  {voidError}
                </p>
              )}
              <input
                type="text"
                value={voidReason}
                onChange={(e) => setVoidReason(e.target.value)}
                placeholder="Motivo (opcional): p. ej. cliente se retractó en contra entrega"
                className="w-full px-3 py-2 text-sm border border-red-200 rounded-md bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-red-300"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setConfirmingVoid(false);
                    setVoidError('');
                  }}
                  className="flex-1 px-4 py-2.5 text-sm border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleVoid}
                  disabled={voiding}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {voiding ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-1.5"></i>Anulando...
                    </>
                  ) : (
                    <>Confirmar anulación</>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>

    {showReceipt && detail && (
      <ReceiptModal
        receipt={{
          title: 'Comprobante de Venta',
          number: detail.saleNumber,
          date: detail.saleDate,
          customerName: detail.customerName,
          salespersonName: detail.salespersonName,
          paymentMethod: detail.paymentMethod,
          paymentStatus: detail.paymentStatus,
          items: detail.items.map((it) => ({
            productName: it.productName,
            sizeLabel: it.sizeName,
            quantity: it.quantity,
            unitPrice: it.unitPrice,
            subtotal: it.subtotal,
          })),
          total: detail.total,
        }}
        onClose={() => {
          setShowReceipt(false);
          setAutoPrintReceipt(false);
        }}
        autoPrint={autoPrintReceipt}
      />
    )}
    </>
  );
}